/**
 * Form Schema Query Hook (Issue #658)
 *
 * GraphQL hooks for querying form and list schemas from the backend.
 * Includes tenant-specific overrides applied at query time.
 */

// eslint-disable-next-line no-restricted-imports -- Data layer hook requires direct Apollo access
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type {
  FormSchema,
  ListSchema,
  FormSchemaOverride,
  FormSchemaOverrideInput,
} from '@/generated/graphql';

// =============================================================================
// GRAPHQL DOCUMENTS
// =============================================================================

/**
 * Query for form schema (with tenant overrides applied)
 */
export const FORM_SCHEMA_QUERY = gql`
  query FormSchema($subgraph: String!, $entityType: String!, $rulesetId: String) {
    formSchema(subgraph: $subgraph, entityType: $entityType, rulesetId: $rulesetId) {
      subgraph
      entityType
      version
      displayName
      description
      safetyLevel
      fields {
        name
        path
        fieldType
        label
        required
        placeholder
        helpText
        defaultValue
        options {
          value
          label
          disabled
          description
        }
        referenceConfig {
          subgraph
          entityType
          query
          valuePath
          labelPath
          searchFields
        }
        visibleWhen {
          field
          operator
          value
        }
        disabledWhen {
          field
          operator
          value
        }
        minLength
        maxLength
        min
        max
        pattern
        patternMessage
        piiClassification
        auditRequired
        maskOnDisplay
        minimumTier
        order
        width
        groupable
      }
      layout {
        layoutType
        sections {
          id
          title
          description
          fields
          order
          collapsible
          collapsed
        }
      }
      validations {
        fields
        expression
        message
      }
      createMutation
      updateMutation
      fetchQuery
      createdAt
      updatedAt
    }
  }
`;

/**
 * Query for list schema
 */
export const LIST_SCHEMA_QUERY = gql`
  query ListSchema($subgraph: String!, $entityType: String!) {
    listSchema(subgraph: $subgraph, entityType: $entityType) {
      subgraph
      entityType
      version
      displayName
      safetyLevel
      columns {
        name
        path
        label
        columnType
        width
        sortable
        filterable
        hidden
        align
        statusConfig {
          statusColors
        }
        piiClassification
        maskOnDisplay
      }
      filters {
        name
        label
        fieldType
        options {
          value
          label
        }
        defaultValue
      }
      actions {
        name
        label
        actionType
        icon
        confirmation
        confirmTitle
        confirmMessage
        mutation
        requiredPermission
        safetyLevel
      }
      defaultSort {
        column
        direction
      }
      listQuery
      pageSize
    }
  }
`;

/**
 * Query for list of available schemas in a subgraph
 */
export const FORM_SCHEMAS_QUERY = gql`
  query FormSchemas($subgraph: String!) {
    formSchemas(subgraph: $subgraph) {
      subgraph
      entityType
      version
      displayName
      safetyLevel
    }
  }
`;

/**
 * Query for tenant override (admin use)
 */
export const FORM_SCHEMA_OVERRIDE_QUERY = gql`
  query FormSchemaOverride($subgraph: String!, $entityType: String!) {
    formSchemaOverride(subgraph: $subgraph, entityType: $entityType) {
      tenantId
      subgraph
      entityType
      hiddenFields
      readonlyFields
      labelOverrides
      customFields {
        name
        fieldType
        label
        required
      }
      customValidations {
        fields
        expression
        message
      }
    }
  }
`;

/**
 * Mutation to save tenant override
 */
export const SAVE_FORM_SCHEMA_OVERRIDE = gql`
  mutation SaveFormSchemaOverride($input: FormSchemaOverrideInput!) {
    saveFormSchemaOverride(input: $input) {
      tenantId
      subgraph
      entityType
    }
  }
`;

// =============================================================================
// HOOKS
// =============================================================================

interface UseFormSchemaOptions {
  subgraph: string;
  entityType: string;
  /** DRR-162: Optional RuleSet ID for service-line-specific form override resolution.
   * Pass from constellation instance's `primary_ruleset` for multi-service-line tenants. */
  rulesetId?: string;
  skip?: boolean;
}

interface UseFormSchemaResult {
  schema: FormSchema | null;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

/**
 * Hook to query form schema for a subgraph entity
 */
export function useFormSchema({
  subgraph,
  entityType,
  rulesetId,
  skip = false,
}: UseFormSchemaOptions): UseFormSchemaResult {
  const { data, loading, error, refetch } = useQuery<{
    formSchema: FormSchema;
  }>(FORM_SCHEMA_QUERY, {
    variables: { subgraph, entityType, rulesetId },
    skip,
    // Cache for 5 minutes (schemas rarely change)
    fetchPolicy: 'cache-first',
  });

  return {
    schema: data?.formSchema ?? null,
    loading,
    error: error as Error | undefined,
    refetch,
  };
}

interface UseListSchemaOptions {
  subgraph: string;
  entityType: string;
  skip?: boolean;
}

interface UseListSchemaResult {
  schema: ListSchema | null;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

/**
 * Hook to query list schema for a subgraph entity
 */
export function useListSchema({
  subgraph,
  entityType,
  skip = false,
}: UseListSchemaOptions): UseListSchemaResult {
  const { data, loading, error, refetch } = useQuery<{
    listSchema: ListSchema;
  }>(LIST_SCHEMA_QUERY, {
    variables: { subgraph, entityType },
    skip,
    fetchPolicy: 'cache-first',
  });

  return {
    schema: data?.listSchema ?? null,
    loading,
    error: error as Error | undefined,
    refetch,
  };
}

interface UseFormSchemasOptions {
  subgraph: string;
  skip?: boolean;
}

interface UseFormSchemasResult {
  schemas: FormSchema[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

/**
 * Hook to list all form schemas for a subgraph
 */
export function useFormSchemas({
  subgraph,
  skip = false,
}: UseFormSchemasOptions): UseFormSchemasResult {
  const { data, loading, error, refetch } = useQuery<{
    formSchemas: FormSchema[];
  }>(FORM_SCHEMAS_QUERY, {
    variables: { subgraph },
    skip,
    fetchPolicy: 'cache-first',
  });

  return {
    schemas: data?.formSchemas ?? [],
    loading,
    error: error as Error | undefined,
    refetch,
  };
}

interface UseFormSchemaOverrideOptions {
  subgraph: string;
  entityType: string;
  skip?: boolean;
}

interface UseFormSchemaOverrideResult {
  override: FormSchemaOverride | null;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

/**
 * Hook to query tenant-specific override (for admin UI)
 */
export function useFormSchemaOverride({
  subgraph,
  entityType,
  skip = false,
}: UseFormSchemaOverrideOptions): UseFormSchemaOverrideResult {
  const { data, loading, error, refetch } = useQuery<{
    formSchemaOverride: FormSchemaOverride;
  }>(FORM_SCHEMA_OVERRIDE_QUERY, {
    variables: { subgraph, entityType },
    skip,
    fetchPolicy: 'cache-first',
  });

  return {
    override: data?.formSchemaOverride ?? null,
    loading,
    error: error as Error | undefined,
    refetch,
  };
}

interface UseSaveFormSchemaOverrideResult {
  saveOverride: (
    input: FormSchemaOverrideInput
  ) => Promise<FormSchemaOverride | null>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook to save tenant-specific form schema override
 */
export function useSaveFormSchemaOverride(): UseSaveFormSchemaOverrideResult {
  const [mutate, { loading, error }] = useMutation<Record<string, any>>(SAVE_FORM_SCHEMA_OVERRIDE);

  const saveOverride = async (input: FormSchemaOverrideInput) => {
    const result = await mutate({
      variables: { input },
      refetchQueries: [FORM_SCHEMA_QUERY, FORM_SCHEMA_OVERRIDE_QUERY],
    });
    return result.data?.saveFormSchemaOverride ?? null;
  };

  return {
    saveOverride,
    loading,
    error: error as Error | undefined,
  };
}

// =============================================================================
// DYNAMIC ENTITY DATA HOOK
// =============================================================================

interface UseEntityDataOptions {
  /** The GraphQL query name from the schema's fetchQuery */
  queryName: string | null | undefined;
  /** Entity ID to fetch */
  entityId: string | null | undefined;
  /** Skip the query */
  skip?: boolean;
}

interface UseEntityDataResult {
  data: Record<string, unknown> | null;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook to fetch entity data using the schema-defined fetchQuery
 *
 * Note: This uses a simplified approach since we can't dynamically
 * build GraphQL queries at runtime. The actual implementation would
 * need the entity query to be pre-defined or use a generic entity endpoint.
 */
export function useEntityData({
  queryName,
  entityId,
  skip = false,
}: UseEntityDataOptions): UseEntityDataResult {
  // For now, return empty data - the actual implementation would need
  // to either use a generic entity endpoint or have pre-defined queries
  // per subgraph/entityType combination
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const shouldSkip = skip || !queryName || !entityId;

  // Placeholder - in production, this would use a registry of entity queries
  // or a generic entity fetch endpoint
  return {
    data: null,
    loading: false,
    error: undefined,
  };
}

// =============================================================================
// LIST DATA HOOK
// =============================================================================

interface UseListDataOptions {
  /** The GraphQL query name from the schema's listQuery */
  queryName: string | null | undefined;
  /** Pagination params */
  first?: number;
  after?: string;
  /** Filter values */
  filter?: Record<string, unknown>;
  /** Search query */
  search?: string;
  /** Skip the query */
  skip?: boolean;
}

interface UseListDataResult {
  rows: Record<string, unknown>[];
  totalCount: number;
  hasNextPage: boolean;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

/**
 * Hook to fetch list data using the schema-defined listQuery
 *
 * Note: This uses a placeholder implementation. The actual implementation
 * would need pre-defined queries or a generic list endpoint.
 */
export function useListData({
  queryName: _queryName,
  first: _first = 20,
  after: _after,
  filter: _filter,
  search: _search,
  skip: _skip = false,
}: UseListDataOptions): UseListDataResult {
  // Placeholder - in production, this would use a registry of list queries
  // or a generic list endpoint
  return {
    rows: [],
    totalCount: 0,
    hasNextPage: false,
    loading: false,
    error: undefined,
    refetch: () => {},
  };
}

export default useFormSchema;
